import React from 'react';
import { makeStyles } from '@material-ui/core';
import Router from 'components/Router/Router';
import ROUTES from 'consts/routes';
import Store from 'store/store';
import Breadcrumb from './Breadcrumb';
import actions from 'store/actions';
import Warning from 'components/Charts/Utils/Warning';

const { connect, selectors } = Store;
const { useRouteMatch, useParams } = Router;

const useStyles = makeStyles((theme: any) => {
  return {
    breadcrumb: {
      left: '224px',
      top: '12px',
      display: 'flex',
      borderRadius: '15px',
      flex: 1,
      height: '30px',
      backgroundColor: '#d4d4d4',
      alignItems: 'center',
      textTransform: 'uppercase',
      margin: '1.25rem 0'
    },
    crumb: {
      color: 'black',
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      cursor: 'pointer',
      flex: 1,
      height: '100%',
      fontWeight: 700,
      textTransform: 'uppercase',
      lineHeight: 1,
      padding: '0 0.25rem',
      '@media (max-width: 1280px)': {
        fontSize: '12px'
      },
      '@media (max-width: 800px)': {
        fontSize: '10px'
      }
    },
    active: {
      color: 'white',
      backgroundColor: theme.palette.primary.main
    },
    disabled: {
      color: '#B2B2B2'
    },
    hovering: {
      color: 'white',
      backgroundColor: theme.palette.primary.light
    },
    inactive: {
      backgroundColor: '#FFFFFF',
      height: '30px',
      cursor: 'default'
    },
    isCurrentIndex: {
      backgroundColor: theme.palette.secondary.main
    }
  };
});

interface RouteType {
  path: string;
  value: string;
  subpath?: string;
}

const breadRoutes: RouteType[] = [
  { path: ROUTES.RISK_SUMMARY.MAIN, value: 'Risk analysis' },
  {
    path: ROUTES.EXISTING_POLICIES.MANAGE_POLICIES,
    value: 'Existing policies'
  },
  { path: ROUTES.EXISTING_POLICIES.POLICY_ANALYSIS, value: 'Policy analysis' },
  {
    path: ROUTES.RECOMMENDATIONS.MAIN,
    value: 'Recommendations',
    subpath: ROUTES.RECOMMENDATIONS.PORTFOLIO_BUILDER
  },
  { path: ROUTES.ADVISOR_CHARGES.MAIN, value: 'Adviser charges' },
  { path: ROUTES.IPS.MAIN, value: 'Summary' }
];

const Breadcrumbs = ({ setNextPage, caseInfo, riskProfiler }: any) => {
  const classes = useStyles();
  const route = useRouteMatch();
  const { portfolio } = useParams() as any;

  return (
    <>
      {riskProfiler !== caseInfo.riskProfiler && (
        <Warning text="This case uses an archived risk profiler and may contain outdated data. Clone it to ensure accuracy for live client work." />
      )}
      <div className={classes.breadcrumb}>
        {breadRoutes.map((routeData: RouteType, index: number) => {
          const currentIndex =
            route &&
            breadRoutes.findIndex(
              breadRoute =>
                breadRoute.path.includes(route.path) ||
                (breadRoute.subpath && breadRoute.subpath.includes(route.path))
            );
          const isActive =
            (route && routeData.path === route.path) ||
            (currentIndex && index < currentIndex);

          const isRecommendedCrumb = routeData.path.includes(
            ROUTES.RECOMMENDATIONS.MAIN
          );
          const isPortfolioBuilder =
            route &&
            route.path.includes(ROUTES.RECOMMENDATIONS.PORTFOLIO_BUILDER);

          const link = routeData.path.replace(':portfolio', portfolio || '');
          const isQuestionnaireOpen =
            route &&
            (route.path.includes(ROUTES.QUESTIONNAIRE.BASE) ||
              route.path.includes(ROUTES.CFL_QUESTIONNAIRE) ||
              route.path.includes(ROUTES.EXP_QUESTIONNAIRE)) &&
            index === 0;

          const isCurrentIndex = currentIndex === index;

          return (
            <Breadcrumb
              key={routeData.path}
              routeData={routeData}
              classes={classes}
              isActive={isActive}
              isPortfolioBuilder={isPortfolioBuilder}
              isRecommendedCrumb={isRecommendedCrumb}
              isQuestionnaireOpen={isQuestionnaireOpen}
              link={link}
              index={index}
              currentIndex={currentIndex}
              isCurrentIndex={isCurrentIndex}
              callback={() => setNextPage(link)}
            />
          );
        })}
      </div>
    </>
  );
};

export default connect(
  (state: any) => ({
    policyOwner: selectors.caseSelectors.caseCustomer(state),
    caseInfo: selectors.caseSelectors.case(state),
    riskProfiler: selectors.firmSelectors.riskProfiler(state)
  }),
  {
    setNextPage: actions.caseActions.setNextPage
  }
)(Breadcrumbs);
